import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { lazy, Suspense } from 'react'

// 加载中组件
function PageLoading() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-gray-500">加载中...</div>
    </div>
  )
}

/**
 * 动态导入失败兜底：当发版后旧 HTML 的用户访问已不存在的 chunk，自动刷新拿新 HTML + 新 chunk
 * 只重试一次，避免死循环
 */
function importWithRetry<T>(importFn: () => Promise<T>): Promise<T> {
  return importFn().catch((err: Error) => {
    const retriedKey = '__chunk_reload_retried__'
    const isChunkError =
      /Failed to fetch dynamically imported module|Loading chunk .* failed|error loading dynamically imported module/i.test(
        err.message || ''
      )
    if (isChunkError && !sessionStorage.getItem(retriedKey)) {
      sessionStorage.setItem(retriedKey, '1')
      window.location.reload()
    }
    throw err
  })
}

// 懒加载包装器
function lazyLoad(importFn: () => Promise<{ default: React.ComponentType<any> }>) {
  const LazyComponent = lazy(() => importWithRetry(importFn))
  return (
    <Suspense fallback={<PageLoading />}>
      <LazyComponent />
    </Suspense>
  )
}

// 懒加载布局
const MobileLayoutWrapper = lazy(() =>
  importWithRetry(() => import('@/components/mobile-layout-wrapper'))
)

// 认证保护路由
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('access_token')
  if (!token) {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

// 公开路由（已登录则跳转）
function PublicRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('access_token')
  if (token) {
    return <Navigate to="/orders" replace />
  }
  return <>{children}</>
}

// Protected Layout 包装器
function ProtectedLayoutWrapper() {
  return (
    <ProtectedRoute>
      <Suspense fallback={<PageLoading />}>
        <MobileLayoutWrapper />
      </Suspense>
    </ProtectedRoute>
  )
}

export const router = createBrowserRouter([
  // 根路径重定向
  {
    path: '/',
    element: <Navigate to="/orders" replace />,
  },

  // 公开路由
  {
    path: '/login',
    element: (
      <PublicRoute>
        {lazyLoad(() => import('@/pages/login'))}
      </PublicRoute>
    ),
  },
  {
    path: '/register',
    element: (
      <PublicRoute>
        {lazyLoad(() => import('@/pages/register'))}
      </PublicRoute>
    ),
  },
  {
    path: '/auth-debug',
    element: lazyLoad(() => import('@/pages/auth-debug')),
  },

  // 微信绑定回调（需已登录，读当前 token 绑 openId）
  {
    path: '/bind-wechat/callback',
    element: (
      <ProtectedRoute>
        {lazyLoad(() => import('@/pages/bind-wechat/callback'))}
      </ProtectedRoute>
    ),
  },

  // 公开检查路由（无需登录）
  {
    path: '/check/order/:id',
    element: lazyLoad(() => import('@/pages/check/order/[id]')),
  },
  {
    path: '/check/device/:id',
    element: lazyLoad(() => import('@/pages/check/device/[id]')),
  },

  // 受保护路由 - 带 MobileLayout
  {
    element: <ProtectedLayoutWrapper />,
    children: [
      {
        path: '/orders',
        element: lazyLoad(() => import('@/pages/orders/index')),
      },
      {
        path: '/orders/search',
        element: lazyLoad(() => import('@/pages/orders/search')),
      },
      {
        path: '/orders/new',
        element: lazyLoad(() => import('@/pages/orders/new')),
      },
      {
        path: '/orders/create',
        element: <Navigate to="/orders/new" replace />,
      },
      {
        path: '/orders/:id',
        element: lazyLoad(() => import('@/pages/orders/[id]')),
      },
      {
        path: '/payments',
        element: lazyLoad(() => import('@/pages/payments/index')),
      },
      {
        path: '/payments/:id',
        element: lazyLoad(() => import('@/pages/payments/[id]')),
      },
      {
        path: '/invoices',
        element: lazyLoad(() => import('@/pages/invoices/index')),
      },
      {
        path: '/invoices/:id',
        element: lazyLoad(() => import('@/pages/invoices/[id]')),
      },
      {
        path: '/certificates',
        element: lazyLoad(() => import('@/pages/certificates/index')),
      },
      {
        path: '/refunds',
        element: lazyLoad(() => import('@/pages/refunds/index')),
      },
      {
        path: '/profile',
        element: lazyLoad(() => import('@/pages/profile/index')),
      },
      {
        path: '/scan',
        element: lazyLoad(() => import('@/pages/scan/index')),
      },
    ],
  },

  // 404 处理
  {
    path: '*',
    element: <Navigate to="/orders" replace />,
  },
])
